import type { BakedEffectManifest, BakedManifest } from './render/bakedManifest';
import hueRampUrl from '../assets/baked/hues/hues.webp?url';
import { staticsManifestIfLoaded } from './render/staticsManifest';
import { staticAtlasPageUrl } from './render/staticAssetUrls';
import fireballManifestJson from '../assets/baked/effects/fireball.json';
import fireballAtlasUrl from '../assets/baked/effects/fireball.webp?url';
import explosionManifestJson from '../assets/baked/effects/explosion.json';
import explosionAtlasUrl from '../assets/baked/effects/explosion.webp?url';
import cardStandIndexJson from '../assets/baked/card-stand-index.json';
import { decodeUoHue } from './uoHue';
import { huePixel, type RgbaPixel } from './render/huePixel';
import { byBaseName, mobileAtlasMap } from './render/mobileAssetUrls';

const battleManifestLoaders = import.meta.glob([
  '../assets/baked/mobiles/human-*-walk.json',
  '../assets/baked/mobiles/human-*-attack-a.json',
  '../assets/baked/mobiles/human-*-attack-b.json',
  '../assets/baked/mobiles/human-*-attack-c.json',
  '!../assets/baked/mobiles/human-400-walk.json',
  '!../assets/baked/mobiles/human-401-walk.json',
], {
  import: 'default',
}) as Record<string, () => Promise<BakedManifest>>;
const battleManifestLoaderMap = byBaseName(battleManifestLoaders);
const presentationEffectManifests = import.meta.glob(
  '../assets/baked/effects/*.json',
  { eager: true, import: 'default' },
) as Record<string, BakedEffectManifest>;
const presentationEffectAtlasUrls = import.meta.glob(
  '../assets/baked/effects/*.webp',
  { eager: true, query: '?url', import: 'default' },
) as Record<string, string>;
const presentationEffectManifestMap = byBaseName(presentationEffectManifests);
const presentationEffectAtlasMap = byBaseName(presentationEffectAtlasUrls);

interface CardStandIndexEntry {
  atlasWidth: number;
  atlasHeight: number;
  directions: BakedManifest['directions'];
  envelopes: Record<string, CardBattleSpriteEnvelope>;
}

interface CardStandIndex {
  version: 1;
  bodies: Record<string, CardStandIndexEntry>;
}

const cardStandIndex = cardStandIndexJson as CardStandIndex;
const battleFrameLoads = new Map<string, Promise<CardBattleSpriteArt[]>>();
const loadedBattleFrames = new Map<string, CardBattleSpriteArt[]>();

export interface CardSpriteArt {
  atlasUrl: string;
  atlasWidth: number;
  atlasHeight: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CardEffectSpriteClip {
  frames: CardSpriteArt[];
  frameIntervalMs: number;
}

/** Baked UO spell frames for DOM battle presentation, absent only when unbaked. */
export function cardEffectSpriteClip(effectId: string): CardEffectSpriteClip | undefined {
  const manifest = presentationEffectManifestMap.get(effectId);
  const atlasUrl = presentationEffectAtlasMap.get(effectId);
  if (!manifest || !atlasUrl || manifest.frames.length === 0) return undefined;
  return {
    frameIntervalMs: manifest.frameIntervalMs,
    frames: manifest.frames.map((frame) => ({
      atlasUrl,
      atlasWidth: manifest.atlasWidth,
      atlasHeight: manifest.atlasHeight,
      x: frame.x,
      y: frame.y,
      width: frame.w,
      height: frame.h,
    })),
  };
}

/** One body-animation crop plus the UO registration point used to keep its feet planted. */
export interface CardBattleSpriteArt extends CardSpriteArt {
  cx: number;
  cy: number;
}

/** Registration envelope for sizing a body's whole stand, walk, and attack family. */
export interface CardBattleSpriteEnvelope {
  horizontalExtent: number;
  extentAboveGround: number;
  extentBelowGround: number;
}

/**
 * Clientless action stubs contain one flat placeholder frame for every facing.
 * They are useful for keeping a bake complete, but must never appear as combat art.
 */
export function isPlaceholderCardBattleAction(
  manifest: Pick<BakedManifest, 'directions'>,
  action: 'stand' | 'walk' | 'attack-a' | 'attack-b' | 'attack-c',
): boolean {
  return action !== 'stand'
    && manifest.directions.length > 0
    && manifest.directions.every((entry) => entry.frames.length <= 1);
}

export type CardActionArtKind = 'fireball' | 'yellow-potion' | 'purple-potion' | 'pouch' | 'scroll';

const ACTION_ART_KINDS: Readonly<Record<string, CardActionArtKind>> = {
  'healing-potion': 'yellow-potion',
  'greater-heal': 'scroll',
  fireball: 'fireball',
  lightning: 'scroll',
  'explosion-potion': 'purple-potion',
  'trapped-pouch': 'pouch',
  teleport: 'scroll',
  spellbook: 'scroll',
  bless: 'scroll',
  'energy-bolt': 'scroll',
  'flame-strike': 'scroll',
  'chain-lightning': 'scroll',
  earthquake: 'scroll',
  resurrection: 'scroll',
  'animate-dead': 'scroll',
  'reactive-armor': 'scroll',
  'magic-reflection': 'scroll',
  'blade-spirits': 'scroll',
  'summon-creature': 'scroll',
  explosion: 'scroll',
  'vengeful-spirit': 'scroll',
  'summon-daemon': 'scroll',
  paralyze: 'scroll',
  'blood-oath': 'scroll',
  'noble-sacrifice': 'scroll',
  'word-of-death': 'scroll',
  'divine-fury': 'scroll',
};

const STATIC_NAME_BY_KIND: Readonly<Partial<Record<CardActionArtKind, string>>> = {
  'yellow-potion': 'card-yellow-potion',
  'purple-potion': 'card-purple-potion',
  pouch: 'card-pouch',
  scroll: 'card-scroll',
};

/**
 * The classic client stores small inventory items inside a 44px registration box.
 * Crop that transparent registration margin for card presentation, while the world
 * keeps using the full static frame for its bottom-centre placement semantics.
 */
const STATIC_CROP_BY_KIND: Readonly<Partial<Record<CardActionArtKind, {
  x: number;
  y: number;
  width: number;
  height: number;
}>>> = {
  'yellow-potion': { x: 13, y: 1, width: 9, height: 17 },
  'purple-potion': { x: 13, y: 1, width: 9, height: 17 },
  pouch: { x: 21, y: 1, width: 11, height: 15 },
  scroll: { x: 6, y: 1, width: 22, height: 19 },
};

export type { RgbaPixel } from './render/huePixel';

/** Apply ClassicUO's encoded body-hue modes to one atlas pixel - the shared
 * software recolour path ({@link ./render/huePixel.huePixel}), re-exported under
 * this module's historical name. */
export const hueCardPixel = huePixel;

const imageLoads = new Map<string, Promise<HTMLImageElement>>();

function loadImage(doc: Document, url: string): Promise<HTMLImageElement> {
  const cached = imageLoads.get(url);
  if (cached) return cached;
  const loaded = new Promise<HTMLImageElement>((resolve, reject) => {
    const ImageCtor = doc.defaultView?.Image;
    if (!ImageCtor) {
      reject(new Error('card art: Image is unavailable'));
      return;
    }
    const image = new ImageCtor();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`card art: failed to load ${url}`));
    image.src = url;
  });
  imageLoads.set(url, loaded);
  return loaded;
}

function canvasContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  try {
    return canvas.getContext('2d', { willReadFrequently: true });
  } catch {
    return null;
  }
}

/** Paint one exactly hued card crop after its atlas and the shared hue ramp load. */
export async function paintHuedCardSprite(
  canvas: HTMLCanvasElement,
  art: CardSpriteArt,
  encodedHue: number,
): Promise<void> {
  const doc = canvas.ownerDocument;
  try {
    const [atlas, hues] = await Promise.all([
      loadImage(doc, art.atlasUrl),
      loadImage(doc, hueRampUrl),
    ]);
    const ctx = canvasContext(canvas);
    if (!ctx) return;

    ctx.clearRect(0, 0, art.width, art.height);
    ctx.drawImage(
      atlas,
      art.x,
      art.y,
      art.width,
      art.height,
      0,
      0,
      art.width,
      art.height,
    );
    const pixels = ctx.getImageData(0, 0, art.width, art.height);

    const mode = decodeUoHue(encodedHue);
    const paletteHue = mode.paletteHue;
    let palette: RgbaPixel[] = [];
    if (!mode.spectral && paletteHue > 0) {
      const row = paletteHue <= hues.height ? paletteHue : 1;
      const ramp = doc.createElement('canvas');
      ramp.width = 32;
      ramp.height = 1;
      const rampCtx = canvasContext(ramp);
      if (!rampCtx) return;
      rampCtx.drawImage(hues, 0, row - 1, 32, 1, 0, 0, 32, 1);
      const data = rampCtx.getImageData(0, 0, 32, 1).data;
      palette = Array.from({ length: 32 }, (_, i) => [
        data[i * 4]!,
        data[i * 4 + 1]!,
        data[i * 4 + 2]!,
        data[i * 4 + 3]!,
      ]);
    }

    for (let i = 0; i < pixels.data.length; i += 4) {
      const next = hueCardPixel(
        [pixels.data[i]!, pixels.data[i + 1]!, pixels.data[i + 2]!, pixels.data[i + 3]!],
        encodedHue,
        palette,
      );
      pixels.data[i] = next[0];
      pixels.data[i + 1] = next[1];
      pixels.data[i + 2] = next[2];
      pixels.data[i + 3] = next[3];
    }
    ctx.putImageData(pixels, 0, 0);
    canvas.style.backgroundImage = 'none';
  } catch {
    // The raw atlas background remains visible if either image cannot load.
  }
}

/** The deliberately small set of visual treatments shared by spell and item cards. */
export function cardActionArtKind(cardId: string): CardActionArtKind | undefined {
  return ACTION_ART_KINDS[cardId];
}

/** Resolve a card's UO effect/item art crop from the committed baked atlases. */
export function cardActionArt(cardId: string): CardSpriteArt | undefined {
  const kind = cardActionArtKind(cardId);
  if (!kind) return undefined;

  if (kind === 'fireball') {
    const manifest = fireballManifestJson as BakedEffectManifest;
    const frame = manifest.frames[0];
    if (!frame) return undefined;
    return {
      atlasUrl: fireballAtlasUrl,
      atlasWidth: manifest.atlasWidth,
      atlasHeight: manifest.atlasHeight,
      x: frame.x,
      y: frame.y + 1,
      width: 46,
      height: 19,
    };
  }

  const staticName = STATIC_NAME_BY_KIND[kind];
  // The statics manifest is a fetched resource (see render/staticsManifest.ts);
  // boot awaits it before any card UI can render, so a miss here only means the
  // caller ran before assets loaded and gets the same no-art degrade as an
  // unknown sprite.
  const manifest = staticsManifestIfLoaded();
  const sprite = manifest?.statics.find((entry) => entry.name === staticName);
  if (!manifest || !sprite) return undefined;
  const page = manifest.pages[sprite.page];
  const atlasUrl = page ? staticAtlasPageUrl(page.atlas) : undefined;
  if (!page || !atlasUrl) return undefined;
  const crop = STATIC_CROP_BY_KIND[kind] ?? { x: 0, y: 0, width: sprite.w, height: sprite.h };
  return {
    atlasUrl,
    atlasWidth: page.width,
    atlasHeight: page.height,
    x: sprite.x + crop.x,
    y: sprite.y + crop.y,
    width: crop.width,
    height: crop.height,
  };
}

/** The Arena explosion's full-bloom frame, used behind the purple potion. */
export function cardActionBackdropArt(cardId: string): CardSpriteArt | undefined {
  if (cardId !== 'explosion-potion') return undefined;
  const manifest = explosionManifestJson as BakedEffectManifest;
  const frame = manifest.frames[7];
  if (!frame) return undefined;
  return {
    atlasUrl: explosionAtlasUrl,
    atlasWidth: manifest.atlasWidth,
    atlasHeight: manifest.atlasHeight,
    x: frame.x + 15,
    y: frame.y + 1,
    width: 56,
    height: 68,
  };
}

/** Resolve one front-facing, idle creature frame for use outside the Pixi world. */
export function cardSpriteArt(body: number): CardSpriteArt | undefined {
  const basename = `human-${body}-stand`;
  const manifest = cardStandIndex.bodies[String(body)];
  const atlasUrl = mobileAtlasMap.get(basename);
  if (!manifest || !atlasUrl) return undefined;

  const frame = manifest.directions.find((direction) => direction.direction === 0)?.frames[0]
    ?? manifest.directions.flatMap((direction) => direction.frames)[0];
  if (!frame) return undefined;

  return {
    atlasUrl,
    atlasWidth: manifest.atlasWidth,
    atlasHeight: manifest.atlasHeight,
    x: frame.x,
    y: frame.y,
    width: frame.w,
    height: frame.h,
  };
}

/** Resolve cached stand frames synchronously so initial card layout never flashes or resizes. */
export function cardStandSpriteFrames(
  body: number,
  direction: number,
): CardBattleSpriteArt[] {
  const basename = `human-${body}-stand`;
  const manifest = cardStandIndex.bodies[String(body)];
  const atlasUrl = mobileAtlasMap.get(basename);
  if (!manifest || !atlasUrl) return [];
  const frames = manifest.directions.find((entry) => entry.direction === direction)?.frames
    ?? manifest.directions[0]?.frames
    ?? [];
  return frames.map((frame) => ({
    atlasUrl,
    atlasWidth: manifest.atlasWidth,
    atlasHeight: manifest.atlasHeight,
    x: frame.x,
    y: frame.y,
    width: frame.w,
    height: frame.h,
    cx: frame.cx,
    cy: frame.cy,
  }));
}

/** Resolve the precomputed whole-family sizing envelope without loading optional action clips. */
export function cardBattleSpriteEnvelope(
  body: number,
  direction: number,
): CardBattleSpriteEnvelope | undefined {
  const entry = cardStandIndex.bodies[String(body)];
  return entry?.envelopes[String(direction)] ?? Object.values(entry?.envelopes ?? {})[0];
}

/**
 * Resolve every baked frame for one creature action and raw UO facing.
 *
 * Card-only expansion creatures deliberately bake only `stand`, so callers should fall
 * back through {@link cardStandSpriteFrames} when an optional action is unavailable.
 * The promise boundary keeps hundreds of full action manifests out of the cold bundle.
 */
export function cardBattleSpriteFrames(
  body: number,
  action: 'walk' | 'attack-a' | 'attack-b' | 'attack-c',
  direction: number,
): Promise<CardBattleSpriteArt[]> {
  const key = `${body}:${action}:${direction}`;
  const cached = battleFrameLoads.get(key);
  if (cached) return cached;
  const loading = loadCardBattleSpriteFrames(body, action, direction)
    .then((frames) => {
      loadedBattleFrames.set(key, frames);
      return frames;
    });
  battleFrameLoads.set(key, loading);
  return loading;
}

async function loadCardBattleSpriteFrames(
  body: number,
  action: 'walk' | 'attack-a' | 'attack-b' | 'attack-c',
  direction: number,
): Promise<CardBattleSpriteArt[]> {
  const basename = `human-${body}-${action}`;
  const loadManifest = battleManifestLoaderMap.get(basename);
  const atlasUrl = mobileAtlasMap.get(basename);
  if (!loadManifest || !atlasUrl) return [];
  let manifest: BakedManifest;
  try {
    manifest = await loadManifest();
  } catch {
    return [];
  }
  if (isPlaceholderCardBattleAction(manifest, action)) return [];

  const frames = manifest.directions.find((entry) => entry.direction === direction)?.frames
    ?? manifest.directions[0]?.frames
    ?? [];
  return frames.map((frame) => ({
    atlasUrl,
    atlasWidth: manifest.atlasWidth,
    atlasHeight: manifest.atlasHeight,
    x: frame.x,
    y: frame.y,
    width: frame.w,
    height: frame.h,
    cx: frame.cx,
    cy: frame.cy,
  }));
}

/** Return already-loaded action frames without ever delaying accepted battle choreography. */
export function cachedCardBattleSpriteFrames(
  body: number,
  action: 'walk' | 'attack-a' | 'attack-b' | 'attack-c',
  direction: number,
): CardBattleSpriteArt[] {
  return loadedBattleFrames.get(`${body}:${action}:${direction}`) ?? [];
}
